export const ANDROID_APK_PATH = '/android/couplespace.apk';
export const ANDROID_VERSION_PATH = '/android/version.json';

const MAX_VERSION_CODE = 2_100_000_000;

export type AndroidReleaseEnvironment = 'dev' | 'prod';

export type AndroidReleaseManifest = {
  versionCode: number;
  sha: string;
  environment: AndroidReleaseEnvironment;
  applicationId: string;
  apkPath: typeof ANDROID_APK_PATH;
};

export function applicationIdForEnvironment(environment: AndroidReleaseEnvironment): string {
  return environment === 'prod' ? 'com.couplespace.app' : 'com.couplespace.dev';
}

export function buildAndroidReleaseManifest(input: {
  versionCode: number;
  sha: string;
  environment: string | undefined;
  applicationId: string;
}): AndroidReleaseManifest {
  const { versionCode, applicationId } = input;
  const sha = input.sha.trim();
  if (!Number.isInteger(versionCode) || versionCode < 1 || versionCode > MAX_VERSION_CODE) {
    throw new Error('ANDROID_VERSION_CODE must be a positive integer Android can install');
  }
  if (input.environment !== 'dev' && input.environment !== 'prod') {
    throw new Error('ENVIRONMENT must be dev or prod');
  }
  if (!sha) {
    throw new Error('BUILD_SHA is required');
  }
  const expectedId = applicationIdForEnvironment(input.environment);
  if (applicationId !== expectedId) {
    throw new Error(`applicationId ${applicationId} does not match environment ${input.environment}`);
  }
  return {
    versionCode,
    sha,
    environment: input.environment,
    applicationId,
    apkPath: ANDROID_APK_PATH,
  };
}

export function parseAndroidReleaseManifest(value: unknown): AndroidReleaseManifest | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (record.environment !== 'dev' && record.environment !== 'prod') {
    return null;
  }
  if (typeof record.sha !== 'string' || record.sha.trim() === '') {
    return null;
  }
  if (typeof record.applicationId !== 'string') {
    return null;
  }
  if (!Number.isInteger(record.versionCode) || (record.versionCode as number) < 1) {
    return null;
  }
  if ((record.versionCode as number) > MAX_VERSION_CODE) {
    return null;
  }
  if (record.apkPath !== ANDROID_APK_PATH) {
    return null;
  }
  if (record.applicationId !== applicationIdForEnvironment(record.environment)) {
    return null;
  }
  return {
    versionCode: record.versionCode as number,
    sha: record.sha.trim(),
    environment: record.environment,
    applicationId: record.applicationId,
    apkPath: ANDROID_APK_PATH,
  };
}

export function installedAndroidVersionCode(raw: string | undefined): number {
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > MAX_VERSION_CODE) {
    return 0;
  }
  return parsed;
}

export function shouldOfferAndroidUpdate(input: {
  installedVersionCode: number;
  installedApplicationId: string;
  manifest: AndroidReleaseManifest;
}): boolean {
  return (
    input.manifest.applicationId === input.installedApplicationId &&
    input.manifest.versionCode > input.installedVersionCode
  );
}

export function androidReleaseUrl(frontendBaseUrl: string, path: string): string {
  return new URL(path, frontendBaseUrl).href;
}

export type AndroidUpdatePhase = 'hidden' | 'permission' | 'installing' | 'error';

export type AndroidUpdateCheckResult = {
  phase: AndroidUpdatePhase;
  message?: string;
};

export async function runAndroidUpdateCheck(input: {
  installedVersionCode: number;
  installedApplicationId: string;
  manifestUrl: string;
  apkUrl: string;
  fetchManifest: (url: string) => Promise<unknown>;
  downloadAndInstall: (url: string) => Promise<{ status: string }>;
  onAvailable?: () => void;
}): Promise<AndroidUpdateCheckResult> {
  let body: unknown;
  try {
    body = await input.fetchManifest(input.manifestUrl);
  } catch {
    return { phase: 'hidden' };
  }
  const manifest = parseAndroidReleaseManifest(body);
  if (
    !manifest ||
    !shouldOfferAndroidUpdate({
      installedVersionCode: input.installedVersionCode,
      installedApplicationId: input.installedApplicationId,
      manifest,
    })
  ) {
    return { phase: 'hidden' };
  }
  if (!input.apkUrl.startsWith('https://')) {
    return { phase: 'error', message: 'APK URL must be https' };
  }
  input.onAvailable?.();
  try {
    const result = await input.downloadAndInstall(input.apkUrl);
    if (result.status === 'permission') {
      return { phase: 'permission' };
    }
    if (result.status === 'installing') {
      return { phase: 'installing' };
    }
    return { phase: 'error', message: 'Update did not start' };
  } catch (error) {
    const message = error instanceof Error && error.message ? error.message : 'Download failed';
    return { phase: 'error', message };
  }
}
